import { track } from "@amplitude/analytics-react-native";
import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import Animated, { FadeInRight, FadeOutLeft } from "react-native-reanimated";
import LoadingBar from "src/components/LoadingBar";
import OpacityView from "src/components/OpacityView";
import PillButton from "src/components/PillButton";
import ShareMenu from "src/components/ShareMenu";
import { SharePayloadData } from "src/components/ShareMenu/types";
import log from "src/helpers/log";
import useMountEffect from "src/hooks/useMountEffect";
import { useInsideModal } from "src/modals";
import InternalModal from "src/modals/configs/InternalModal";
import {
	paddingLarge,
	paddingMedium,
	paddingSmall,
	paddingSmallSpacer,
	paddingXL,
	paddingXXXL,
} from "src/styles/spacing";
import { AnimatePresence, Image, Text, useSafeArea } from "src/swsh-native";
import { useImageGenerationModalDimensions } from "./ImageGenerationModalSkeleton";

const ImageGenerationModal = () => {
	const style = useImageGenerationModalDimensions();
	const { props, closeSelf } = useInsideModal<InternalModal["ImageGeneration"]>();
	const [isLoading, setIsLoading] = useState(true);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const safeArea = useSafeArea();
	const shuffledBlurbs = useMemo(() => {
		if (!props.loadingBlurbs || props.loadingBlurbs.length === 0) return ["Loading..."];
		return [...props.loadingBlurbs].sort(() => Math.random() - 0.5);
	}, [props.loadingBlurbs]);
	useMountEffect(() => {
		props.imagePromise
			.then(({ imageUrl }) => {
				setImageUrl(imageUrl);
			})
			.catch((err) => {
				log.error({ err }, "Error generating image in ImageGenerationModal");
				closeSelf();
			});
	}, []);

	const [blurbIndex, setBlurbIndex] = useState(0);
	useEffect(() => {
		if (!isLoading || shuffledBlurbs.length === 0) return;
		const interval = setInterval(() => {
			setBlurbIndex((prevIndex) => (prevIndex + 1) % shuffledBlurbs.length);
		}, 5000);
		return () => clearInterval(interval);
	}, [shuffledBlurbs.length, isLoading]);

	const data = useMemo<SharePayloadData | null>(() => {
		if (!imageUrl) return null;
		return [{ type: "Image", url: imageUrl }];
	}, [imageUrl]);
	const { bottomLoadingText, renderShare } = props;

	return (
		<View style={style} testID="ImageGenerationModal">
			<AnimatePresence>
				{isLoading && (
					<OpacityView
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<View
							style={{
								height: 50,
								width: "100%",
							}}
						>
							<Animated.View
								style={{
									position: "absolute",
									alignItems: "center",
									justifyContent: "center",
									width: "100%",
									height: "100%",
									paddingHorizontal: paddingLarge,
								}}
								entering={FadeInRight}
								exiting={FadeOutLeft}
								key={shuffledBlurbs[blurbIndex]}
							>
								<Text
									style={{
										textAlign: "center",
									}}
								>
									{shuffledBlurbs[blurbIndex]}
								</Text>
							</Animated.View>
						</View>
						<View
							style={{
								width: 200,
							}}
						>
							<LoadingBar
								complete={!!imageUrl}
								estimatedTime={30_000}
								onAnimationComplete={() => {
									setIsLoading(false);
								}}
							/>
						</View>
						{!!bottomLoadingText && bottomLoadingText.length > 0 && (
							<View
								style={{
									position: "absolute",
									bottom: paddingSmallSpacer,
									rowGap: paddingSmall,
									paddingHorizontal: paddingLarge,
								}}
							>
								{bottomLoadingText.map((text, index) => (
									<Text
										key={index}
										color="TextLight"
										style={{
											textAlign: "center",
										}}
									>
										{text}
									</Text>
								))}
							</View>
						)}
					</OpacityView>
				)}
			</AnimatePresence>
			{!!imageUrl && !isLoading && (
				<OpacityView
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						paddingTop: safeArea.top,
					}}
				>
					<Image
						alt="Generated Image"
						testID="ImageGenerationModalImage"
						src={imageUrl}
						style={{
							flex: 1,
							marginHorizontal: paddingXXXL,
							marginTop: paddingSmallSpacer,
						}}
						dim="auto"
						contentFit="contain"
					/>
					<View
						style={{
							marginTop: paddingMedium,
							paddingBottom: paddingLarge + safeArea.bottom,
						}}
					>
						{!!data &&
							(renderShare ? (
								renderShare({ imageUrl, data })
							) : (
								<ShareMenu
									data={data}
									onSuccess={() => {
										track("imageGen_share");
									}}
									onComplete={() => {
										closeSelf();
									}}
								/>
							))}
					</View>
				</OpacityView>
			)}
			<PillButton
				blur
				containerStyle={{
					position: "absolute",
					top: safeArea.top + paddingXL,
					right: paddingXL,
				}}
				onPress={() => {
					track("imageGen_dismiss", {
						didGenerateUrl: !!imageUrl,
					});
					closeSelf();
				}}
			>
				Dismiss
			</PillButton>
		</View>
	);
};
export default ImageGenerationModal;
